import { Component, linkedSignal, OnInit, signal } from '@angular/core';
import { AddProductComponent } from '../../modals/add-product/add-product.component';
import { UserDataService } from '../../services/user-data.service';
import { RegisteredSerialsService } from '../../services/registered-serials.service';
import { RegisteredSerial } from '../../models/registered-serial';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UpperCasePipe } from '@angular/common';
import { WarrantyUploadComponent } from '../../modals/warranty-upload/warranty-upload.component';
import { WarrantyOKComponent } from '../../modals/warranty-ok/warranty-ok.component';
import { CouponOKComponent } from '../../modals/coupon-ok/coupon-ok.component';
import { CouponRequest } from '../../models/coupon-request';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from '../../services/utils.service';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { ModalDismissReasons, ModalService } from '../../services/modal.service';
import dayjs from 'dayjs';

@Component({
  selector: 'app-products',
  imports: [FormatDatePipe, TranslatePipe, UpperCasePipe],
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit {

  firstName = linkedSignal(() => this.userDataService.user()?.firstName ?? '');
  registeredSerials = signal<Array<RegisteredSerial>>([]);
  trainers = linkedSignal(() => {
    this.refreshTrainersName();
    return this.userDataService.trainers();
  });

  constructor(
    private userDataService: UserDataService,
    private registeredSerialsService: RegisteredSerialsService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private utils: Utils,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  private refresh() {
    this.spinner.show();
    this.registeredSerialsService.getRegisteredSerials().subscribe({
      next: page => {
        this.spinner.hide();
        this.registeredSerials.set(page.items);
        this.refreshTrainersName();
        this.refreshExpiredFlag();
      },
      error: error => {
        this.spinner.hide();
        this.utils.showMessage(error, this.translate.instant('network_request_error'));
      }
    });
  }

  private refreshTrainersName() {
    const registeredSerials = this.registeredSerials();
    const trainers = this.userDataService.trainers();
    registeredSerials.forEach(item => {
      const index = trainers.findIndex(t => {
        return t.code === item.trainerCode;
      });
      if (index != -1) {
        item.trainerName = trainers[index].name;
      } else {
        item.trainerName = '-';
      }
    });
  }

  private refreshExpiredFlag() {
    const today = dayjs(dayjs().format('YYYY-MM-DD'));
    const registeredSerials = this.registeredSerials();
    registeredSerials.forEach(item => {
      if (item.purchaseDate) {
        const purchaseDate = dayjs(item.purchaseDate);
        const termDate = purchaseDate.add(item.warrantyYears, 'year');
        item.expired = termDate.diff(today) < 0;
      }
    });
  }

  addProduct() {
    this.modalService.show(AddProductComponent).dismissed.subscribe({
      next: reason => {
        if (reason === ModalDismissReasons.OK) {
          this.refresh();
        }
      }
    });
  }

  checkWarrantyExtensionEnabled(code: number): boolean {
    const trainers = this.trainers();
    const index = trainers.findIndex(t => {
      return t.code === code;
    });
    if (index != -1) {
      return trainers[index].warranty_extension_enabled;
    }
    return false;
  }

  getTrainerImageURL(code: number): string {
    return `https://s3.eu-west-1.amazonaws.com/com.elite.repository/assets/trainers/${code}_t.jpg`
  }

  openWarrantyUpload(item: RegisteredSerial) {
    const modal = this.modalService.show(WarrantyUploadComponent, {
      selectedId: item.id
    });
    modal.dismissed.subscribe({
      next: reason => {
        if (reason === ModalDismissReasons.OK) {
          this.onFileUploaded(item);
        }
      }
    });
  }

  private onFileUploaded(item: RegisteredSerial) {
    const modal = this.modalService.show(WarrantyOKComponent, {
      selectedId: item.id
    });
    modal.dismissed.subscribe({
      next: reason => {
        if (reason === ModalDismissReasons.OK) {
          this.refresh();
        }
      }
    });
  }

  couponPressed(item: RegisteredSerial) {
    const data: CouponRequest = {
      serialCode: item.serialCode,
      trainerCode: item.trainerCode,
      lang: this.translate.currentLang() ?? 'en'
    };
    this.spinner.show();
    this.registeredSerialsService.postCoupon(data).subscribe({
      next: response => {
        this.spinner.hide();
        const modal = this.modalService.show(CouponOKComponent, {
          response: response
        });
        modal.dismissed.subscribe({
          next: _ => {
            this.refresh();
          }
        });
      },
      error: error => {
        this.spinner.hide();
        this.utils.showMessage(error, this.utils.getErrorMessage(error, this.userDataService.user()?.email ?? ''));
      }
    });
  }
}
